import { ChevronLeft, Square, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Footer } from "@/components/footer";
import { Header } from "@/components/header";

interface Villa {
  id: number;
  villa_number: number;
  name: string;
  description: string;
  size_sqm: number;
  max_guests: number;
  amenities: string[];
  price_low_season: string;
  price_high_season: string;
  price_currency: string;
}

interface PricingBreakdown {
  nights: number;
  breakdown: {
    lowSeasonNights: number;
    lowSeasonRate: number;
    lowSeasonTotal: number;
    highSeasonNights: number;
    highSeasonRate: number;
    highSeasonTotal: number;
  };
  totalAmount: number;
  currency: string;
  isHighSeason: boolean;
  isMixedSeason: boolean;
}

export function BookSelectVillaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const checkIn = searchParams.get("checkIn");
  const checkOut = searchParams.get("checkOut");

  const [availableVillas, setAvailableVillas] = useState<Villa[]>([]);
  const [pricing, setPricing] = useState<PricingBreakdown | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!checkIn || !checkOut) {
      navigate("/book");
      return;
    }

    fetchAvailability();
    fetchPricing();
  }, [checkIn, checkOut]);

  const fetchAvailability = async () => {
    try {
      const response = await fetch(
        `/api/reservations/availability?checkIn=${checkIn}&checkOut=${checkOut}`
      );
      const data = (await response.json()) as {
        success: boolean;
        availableVillas: Villa[];
      };
      if (data.success) {
        setAvailableVillas(data.availableVillas);
      }
    } catch (error) {
      console.error("Failed to fetch availability:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await fetch(
        `/api/pricing/calculate?checkIn=${checkIn}&checkOut=${checkOut}`
      );
      const data = (await response.json()) as { success: boolean } & PricingBreakdown;
      if (data.success) {
        setPricing(data);
      }
    } catch (error) {
      console.error("Failed to fetch pricing:", error);
    }
  };

  const handleSelectVilla = (villaId: number) => {
    navigate(`/book/guest-details?checkIn=${checkIn}&checkOut=${checkOut}&villaId=${villaId}`);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Get villa image based on villa number
  const getVillaImage = (villaNumber: number) => {
    const images = [
      "/images/villas/villa1.jpg",
      "/images/villas/villa2.jpg",
      "/images/villas/villa3.jpg",
      "/images/villas/villa4.jpg",
      "/images/villas/villa5.jpg",
      "/images/villas/villa6.jpg",
      "/images/villas/villa7.jpg",
      "/images/villas/villa1.jpg",
    ];
    return images[(villaNumber - 1) % images.length];
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-stone-50">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full size-12 border-b-2 border-stone-900" />
            <p className="mt-4 text-stone-600">Loading available villas...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-stone-50">
      <Header />

      <main className="flex-1 py-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Back button */}
          <button
            onClick={() => navigate("/book")}
            className="flex items-center gap-2 text-stone-600 hover:text-stone-900 mb-8 transition-colors"
          >
            <ChevronLeft className="size-5" />
            Change Dates
          </button>

          {/* Header */}
          <div className="mb-8">
            <h1 className="font-['Libre_Baskerville'] text-4xl md:text-5xl font-bold text-stone-900 mb-4">
              Available Villas
            </h1>
            <p className="text-stone-600 text-lg">
              {formatDate(checkIn!)} - {formatDate(checkOut!)} • {pricing?.nights}{" "}
              {pricing?.nights === 1 ? "night" : "nights"}
            </p>
          </div>

          {availableVillas.length === 0 ? (
            <div className="bg-white border border-stone-200 p-12 text-center rounded-lg">
              <p className="text-xl text-stone-600 mb-4">No villas available for these dates</p>
              <button
                onClick={() => navigate("/book")}
                className="bg-stone-900 text-white px-8 py-3 text-sm tracking-widest uppercase font-semibold hover:bg-stone-800 transition-colors"
              >
                Choose Different Dates
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {availableVillas.map((villa) => (
                <div
                  key={villa.id}
                  className="bg-white border border-stone-200 overflow-hidden rounded-lg hover:border-stone-400 transition-colors"
                >
                  <div className="grid md:grid-cols-2 gap-0">
                    {/* Villa Image */}
                    <div className="relative h-72 md:h-auto">
                      <img
                        src={getVillaImage(villa.villa_number)}
                        alt={villa.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 text-xs font-semibold tracking-wider">
                        AVAILABLE
                      </div>
                    </div>

                    {/* Villa Details */}
                    <div className="p-6 md:p-8 flex flex-col">
                      <div className="flex-1">
                        <h3 className="font-['Libre_Baskerville'] text-2xl font-bold mb-3">
                          {villa.name}
                        </h3>

                        {/* Quick specs */}
                        <div className="flex gap-4 text-sm text-stone-600 mb-4">
                          <div className="flex items-center gap-1">
                            <Square className="size-4" />
                            {villa.size_sqm} sqm
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="size-4" />
                            Up to {villa.max_guests} guests
                          </div>
                        </div>

                        <p className="text-stone-600 mb-4 line-clamp-2">{villa.description}</p>

                        {/* Amenities preview */}
                        {villa.amenities && villa.amenities.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {villa.amenities.slice(0, 3).map((amenity, index) => (
                              <span
                                key={index}
                                className="text-xs bg-stone-100 text-stone-700 px-2 py-1 rounded"
                              >
                                {amenity}
                              </span>
                            ))}
                            {villa.amenities.length > 3 && (
                              <span className="text-xs bg-stone-100 text-stone-700 px-2 py-1 rounded">
                                +{villa.amenities.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Pricing */}
                      <div className="bg-stone-50 p-4 rounded mb-4">
                        <div className="text-xs text-stone-500 uppercase tracking-wider mb-2">
                          Price Breakdown
                        </div>
                        <div className="space-y-1 text-sm">
                          {pricing && (
                            <>
                              {pricing.breakdown.lowSeasonNights > 0 && (
                                <div className="flex justify-between">
                                  <span>
                                    ${pricing.breakdown.lowSeasonRate}/night ×{" "}
                                    {pricing.breakdown.lowSeasonNights} nights
                                  </span>
                                  <span className="font-semibold">
                                    ${pricing.breakdown.lowSeasonTotal}
                                  </span>
                                </div>
                              )}
                              {pricing.breakdown.highSeasonNights > 0 && (
                                <div className="flex justify-between">
                                  <span>
                                    ${pricing.breakdown.highSeasonRate}/night ×{" "}
                                    {pricing.breakdown.highSeasonNights} nights
                                  </span>
                                  <span className="font-semibold">
                                    ${pricing.breakdown.highSeasonTotal}
                                  </span>
                                </div>
                              )}
                              {pricing.isMixedSeason && (
                                <div className="text-xs text-amber-700 mt-1">
                                  ⓘ Mixed season rates apply
                                </div>
                              )}
                              {pricing.isHighSeason && !pricing.isMixedSeason && (
                                <div className="text-xs text-amber-700 mt-1">
                                  ✓ High Season Rate
                                </div>
                              )}
                              {!pricing.isHighSeason && (
                                <div className="text-xs text-green-700 mt-1">✓ Low Season Rate</div>
                              )}
                            </>
                          )}
                        </div>
                        <div className="border-t border-stone-300 mt-3 pt-3 flex justify-between font-bold">
                          <span>Total</span>
                          <span className="text-lg">
                            ${pricing?.totalAmount || 0} {pricing?.currency}
                          </span>
                        </div>
                      </div>

                      <button
                        onClick={() => handleSelectVilla(villa.id)}
                        className="w-full bg-stone-900 text-white px-6 py-3 text-sm tracking-widest uppercase font-semibold hover:bg-stone-800 transition-colors"
                      >
                        Select This Villa
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
